import * as Sentry from '@sentry/react';

// Global unhandled rejection handler with better error context
export function setupGlobalErrorHandlers() {
  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    
    // Convert non-Error rejections to proper Error objects for better tracking
    let error;
    if (event.reason instanceof Error) {
      error = event.reason;
    } else if (typeof event.reason === 'string') {
      // Create a proper error with stack trace
      error = new Error(`Promise rejected with: ${event.reason}`);
      error.name = 'UnhandledPromiseRejection';
      
      // Add context about what was happening
      if (event.reason === 'Timeout' || event.reason.includes('timeout')) {
        error.name = 'TimeoutError';
        
        // Try to determine what timed out based on recent activity
        const possibleSources = [];
        
        // Check if socket is connecting
        const socketState = window.__SOCKET_STATE;
        if (socketState && !socketState.connected) {
          possibleSources.push('Socket.IO connection');
        }
        
        // Check if fingerprint was recently generated
        if (window.__FINGERPRINT_GENERATING) {
          possibleSources.push('Fingerprint generation');
        }
        
        // Check if auth refresh was in progress
        if (window.__AUTH_REFRESHING) {
          possibleSources.push('Firebase auth token refresh');
        }
        
        error.message = `Timeout occurred. Possible sources: ${possibleSources.join(', ') || 'Unknown'}`;
      }
    } else {
      error = new Error(`Promise rejected with non-error value: ${JSON.stringify(event.reason)}`);
      error.name = 'UnhandledPromiseRejection';
    }
    
    // Send to Sentry with context
    Sentry.captureException(error, {
      tags: {
        error_type: 'unhandled_rejection',
        rejection_value: String(event.reason),
      },
      extra: {
        promise: event.promise,
        reason: event.reason,
        timestamp: new Date().toISOString(),
      },
    });
    
    // Prevent default browser error handling
    event.preventDefault();
  });
  
  // Also catch regular errors
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    
    // Send to Sentry (it will dedupe if already captured)
    Sentry.captureException(event.error || event, {
      tags: {
        error_type: 'window_error',
      },
      extra: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });
}

// Helper to track async operations
export function trackAsyncOperation(name, operation) {
  const startTime = Date.now();
  
  return Promise.resolve(operation)
    .catch((error) => {
      const duration = Date.now() - startTime;
      
      // Enhanced error with context
      if (typeof error === 'string' && error.includes('Timeout')) {
        const enhancedError = new Error(`${name} timed out after ${duration}ms`);
        enhancedError.name = 'TimeoutError';
        enhancedError.originalError = error;
        throw enhancedError;
      }
      
      throw error;
    });
}